/* eslint-disable @typescript-eslint/no-unused-vars */

import {
    SliceFactory,
    FetchCollectionData,
    FetchItemDataType,
    ApiCollectionResponse,
    filtersToElasticQuery,
    FetchCollectionInputType,
} from '@europrocurement/l2d-redux-utils';
import {
    ConfigurationParameters as AchatsConfigurationParameters,
    Configuration,
    MediaObjectApi,
    MediaObjectApiListMediaObjectMediaObjectCollectionRequest,
    MediaObjectApiListMyMediaObjectMediaObjectCollectionRequest,
    MediaObjectJsonldMediaObjectReadTimestampableBlameambleGroupTaggableGroup,
    MediaObjectNoteJsonldMediaObjectReadTimestampableBlameambleGroupTaggableGroup,
} from '../../../openApi/ApiAchats';
import { Order } from '../../../types/apiGenericTypes';
import { ACHATS_REDUCER_NAME } from '../constants';
// import { UseKeycloakService } from '@europrocurement/l2d-keycloak';

export type MediaObjectApiObject =
    MediaObjectJsonldMediaObjectReadTimestampableBlameambleGroupTaggableGroup &
        Record<string, unknown>;

export type MediaObject = MediaObjectApiObject;
export type MediaObjectNote =
    MediaObjectNoteJsonldMediaObjectReadTimestampableBlameambleGroupTaggableGroup;

export const MEDIAOBJECT_SLICE_NAME = 'mediaobject';

export const UNREGISTERED_INVOICES_DATASOURCE_NAME = 'unregisteredInvoices';
export const USER_UNREGISTERED_INVOICES_DATASOURCE_NAME = 'userUnregisteredInvoices';
export const UNASSIGNED_UNREGISTERED_INVOICES_DATASOURCE_NAME = 'unassignedUnregisteredInvoices';

export const additionalMediaObjectsDataSources: Array<string> = [
    UNREGISTERED_INVOICES_DATASOURCE_NAME,
    USER_UNREGISTERED_INVOICES_DATASOURCE_NAME,
    UNASSIGNED_UNREGISTERED_INVOICES_DATASOURCE_NAME,
];

const mapperMediaObject = function (mediaobject: MediaObjectApiObject) {
    return {
        ...mediaobject,
    } as MediaObject;
};

/**
 * Détermine la période pendant laquelle un MediaObject est en cours d'utilisation.
 * Après cette période, peu importe si quelqu'un est toujours dessus, le MediaObject est de nouveau disponible.
 */
export const dateOffsetMediaObject = 10 * 60 * 1000;

/**
 * Test si un MediaObject est en cours d'utilisation ou non.
 *
 * @param mediaObject MediaObject
 * @returns boolean
 */
export const isProcessingMediaObject = (mediaObject: MediaObject): boolean => {
    if (!mediaObject.dateDebutTraitement) {
        return false;
    }

    if (mediaObject.dateDebutTraitement) {
        if (
            new Date(mediaObject.dateDebutTraitement) >
            new Date(new Date().getTime() - dateOffsetMediaObject)
        ) {
            return true;
        }
    }

    return false;
};

export function mediaObjectFiltersToElasticQuery({
    search,
    pagination,
    filters,
    orders,
    xIdSociete,
}: Omit<FetchCollectionInputType, 'rootstate' | 'options' | 'filters' | 'preFilters'> & {
    filters: Record<string, unknown>;
}) {
    const additionalMatch = [];

    const {
        createdBy,
        linkedFacture,
        available,
        forceInterne,
        processingFacture,
        createdByMe,
        updatedBy,
        dateDebutTraitementBefore,
        ...innerFilters
    } = filters;

    if (createdBy && createdBy !== '') {
        additionalMatch.push({
            term: {
                'createdBy.keyword': createdBy,
            },
        });
    }

    if (updatedBy && updatedBy !== '') {
        additionalMatch.push({
            term: {
                'updatedBy.keyword': updatedBy,
            },
        });
    }

    if (linkedFacture !== undefined) {
        additionalMatch.push({
            term: {
                linkedFacture,
            },
        });
    }

    if (available !== undefined) {
        additionalMatch.push({
            term: {
                available,
            },
        });
    }

    if (forceInterne !== undefined) {
        additionalMatch.push({
            term: {
                forceInterne,
            },
        });
    }

    if (processingFacture === true) {
        additionalMatch.push({
            range: {
                dateDebutTraitement: {
                    lt: new Date(new Date().getTime() + dateOffsetMediaObject),
                    gt: new Date(new Date().getTime() - dateOffsetMediaObject),
                },
            },
        });
    }

    if (processingFacture === false) {
        additionalMatch.push({
            bool: {
                should: [
                    {
                        range: {
                            dateDebutTraitement: {
                                lt: new Date(new Date().getTime() - dateOffsetMediaObject),
                            },
                        },
                    },
                    {
                        bool: {
                            must_not: [
                                {
                                    exists: {
                                        field: 'dateDebutTraitement',
                                    },
                                },
                            ],
                        },
                    },
                ],
            },
        });
    }

    if (dateDebutTraitementBefore) {
        additionalMatch.push({
            bool: {
                should: [
                    {
                        range: {
                            dateDebutTraitement: {
                                lt: dateDebutTraitementBefore,
                            },
                        },
                    },
                    {
                        bool: {
                            must_not: [
                                {
                                    exists: {
                                        field: 'dateDebutTraitement',
                                    },
                                },
                            ],
                        },
                    },
                ],
            },
        });
    }

    // exclude codesRejets and categories from innerFilters
    const { codesRejets, categories, ...cleanedInnerFilters } = innerFilters;

    const requestParameters: MediaObjectApiListMediaObjectMediaObjectCollectionRequest = {
        page: pagination.page + 1,
        itemsPerPage: pagination.itemsPerPage,
        search: filtersToElasticQuery(
            {
                idSociete: xIdSociete,
                ...cleanedInnerFilters,
            },
            additionalMatch,
            orders,
        ),
    };
    return requestParameters;
}

export function createMediaObjectSlice(configuration: AchatsConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new MediaObjectApi(conf);

    const fetchMediaObjectsCollection: FetchCollectionData<MediaObjectApiObject> = ({
        search,
        pagination,
        filters,
        orders,
        xIdSociete,
        rootstate,
    }) => {
        const parameters = mediaObjectFiltersToElasticQuery({
            search,
            pagination,
            filters,
            orders,
            xIdSociete,
        });

        if (filters.createdByMe === true) {
            // Fonction spécifique pour les created by me
            return api
                .listMyMediaObjectMediaObjectCollection(parameters)
                .then((res) => res.data) as Promise<ApiCollectionResponse<MediaObjectApiObject>>;
        }

        return api
            .listMediaObjectMediaObjectCollection(parameters)
            .then((res) => res.data) as Promise<ApiCollectionResponse<MediaObjectApiObject>>;
    };

    const fetchMediaObjectsItem: FetchItemDataType<MediaObjectApiObject> = ({
        idItem,
        search,
        pagination,
        filters,
        orders,
        xIdSociete,
        rootstate,
    }) =>
        api
            .getMediaObjectItem({
                id: String(idItem),
            })
            .then((res) => res.data) as Promise<MediaObjectApiObject>;

    const { slice: mediaobjectSlice, dataSourcesThunks: mediaobjectDataSourcesThunks } =
        SliceFactory.createSlice<MediaObjectApiObject, MediaObject>(
            MEDIAOBJECT_SLICE_NAME,
            ACHATS_REDUCER_NAME,
            additionalMediaObjectsDataSources,
            fetchMediaObjectsCollection,
            fetchMediaObjectsItem,
            mapperMediaObject,
            {
                processingFacture: false,
                available: true,
                deleted: false,
            },
            undefined,
            [
                {
                    field: 'createdAt',
                    value: 'desc',
                },
            ],
        );

    return { mediaobjectSlice, mediaobjectDataSourcesThunks };
}
